import { apiClient } from './api/apiClient';
import {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  User,
  ApiResponse,
} from '../types';

class AuthService {
  /**
   * Login utente
   */
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await apiClient.post<ApiResponse<AuthResponse>>(
      '/auth/login',
      credentials
    );

    if (response.success && response.data) {
      // Salva token e user in localStorage
      apiClient.setToken(response.data.token);
      this.setUser(response.data.user);
      return response.data;
    }

    throw new Error(response.message || 'Login fallito');
  }

  /**
   * Registrazione nuovo utente
   */
  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await apiClient.post<ApiResponse<AuthResponse>>(
      '/auth/register',
      data
    );

    if (response.success && response.data) {
      // Salva token e user in localStorage
      apiClient.setToken(response.data.token);
      this.setUser(response.data.user);
      return response.data;
    }

    throw new Error(response.message || 'Registrazione fallita');
  }

  /**
   * Ottieni informazioni utente corrente
   */
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<ApiResponse<User>>('/auth/me');

    if (response.success && response.data) {
      this.setUser(response.data);
      return response.data;
    }

    throw new Error('Impossibile ottenere informazioni utente');
  }

  /**
   * Logout utente
   */
  logout(): void {
    apiClient.clearToken();
    localStorage.removeItem('user');
    window.location.href = '/login';
  }

  /**
   * Verifica se l'utente è autenticato
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    return !!token;
  }

  /**
   * Ottieni utente salvato in localStorage
   */
  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;

    try {
      return JSON.parse(userStr) as User;
    } catch {
      return null;
    }
  }

  /**
   * Salva utente in localStorage
   */
  private setUser(user: User): void {
    localStorage.setItem('user', JSON.stringify(user));
  }

  /**
   * Verifica se l'utente ha un ruolo specifico
   */
  hasRole(role: string): boolean {
    const user = this.getStoredUser();
    return user?.role.toString() === role;
  }

  /**
   * Verifica se l'utente ha uno dei ruoli specificati
   */
  hasAnyRole(roles: string[]): boolean {
    const user = this.getStoredUser();
    if (!user) return false;
    return roles.some((role) => user.role.toString() === role);
  }
}

// Export singleton instance
export const authService = new AuthService();
